import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { MessageSquare, TriangleAlert } from 'lucide-react';
import { useReducedMotion } from 'motion/react';
import { DURATION_BASE, EASE_OUT_CSS, Loader } from '@strivelabs/ui-core/v2';
import { cn } from '@strivelabs/ui-core/lib/utils';
import { createPortal } from 'react-dom';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
  type DropAnimation,
} from '@dnd-kit/core';
import { useAppStore } from '@/store/appStore';
import { getChatWorkflowInstanceId } from '@/utils/featureFlags';
import type { WorkflowExecution } from '@/hooks/useWorkflowExecutions';
import { ChatStatusDot } from '@/components/chats/ChatStatusDot';
import { RenameDialog } from '@/components/chats/RenameDialog';
import { DeleteDialog } from '@/components/chats/DeleteDialog';
import { titleOf } from '@/components/chats/chatTitle';
import { useChatList } from '../../hooks/useChatList';
import { useChatActions } from '../../hooks/useChatActions';
import { ChatGroup } from './ChatGroup';
import { ChatSearch } from './ChatSearch';

/** Settles a dropped row back onto the token scale rather than dnd-kit's 250ms default. */
const DROP_ANIMATION: DropAnimation = {
  duration: DURATION_BASE * 1000,
  easing: EASE_OUT_CSS,
};

function EmptyState({ isSearching }: { isSearching: boolean }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-4 py-12 text-v2-sidebar-foreground/70">
      <MessageSquare size={20} />
      <p className="text-pretty text-center text-v2-xs">
        {isSearching ? 'No chats match your search' : 'No conversations yet'}
      </p>
    </div>
  );
}

/**
 * Shown when the list request fails.
 *
 * Without this a failed fetch falls through to the empty state, which tells the
 * user they have no conversations — a claim the sidebar is in no position to
 * make when it never heard back.
 */
function ErrorState() {
  return (
    <div role="status" className="flex flex-col items-center justify-center gap-2 px-4 py-12 text-v2-sidebar-foreground/70">
      <TriangleAlert size={20} />
      <p className="text-pretty text-center text-v2-xs">Could not load your chats</p>
    </div>
  );
}

/** The floating copy of a row that follows the pointer while dragging; the row under it just dims. `DragOverlay` sizes the wrapper from the dragged node's own rect, so this fills it rather than fixing its own width. */
function DragPreviewRow({ execution }: { execution: WorkflowExecution }) {
  return (
    <div className="flex h-[26px] w-full cursor-grabbing items-center gap-1.5 rounded-v2-lg bg-v2-sidebar-active px-2 text-[13px] leading-[18px] text-v2-sidebar-accent-foreground shadow-md">
      <ChatStatusDot busy={false} />
      <span className="truncate">{titleOf(execution)}</span>
    </div>
  );
}

/** Drop target for a pinned row dragged out: the rest of the list, below the pinned shelf. */
function UnpinnedDropZone({ children }: { children: ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id: 'unpinned' });
  return (
    <div
      ref={setNodeRef}
      className={cn('flex flex-col transition-colors duration-v2-quick', isOver && 'rounded-v2-lg bg-v2-sidebar-accent/60')}
    >
      {children}
    </div>
  );
}

/**
 * The Chat tab: search, conversations bucketed into a pinned shelf plus
 * relative-date groups, and infinite scroll. Rename, delete and pin are
 * reached from each row's menu, or by dragging a row onto or off the pinned
 * shelf.
 */
export function ChatTab() {
  const { executionId } = useParams<{ executionId?: string }>();
  const agentInstances = useAppStore(state => state.agentInstances);
  const workflowInstanceId = getChatWorkflowInstanceId(agentInstances) ?? '';

  const list = useChatList(workflowInstanceId);
  const actions = useChatActions(workflowInstanceId);
  const reduce = useReducedMotion();

  const sentinelRef = useRef<HTMLDivElement>(null);
  const { fetchNextPage, hasNextPage, isFetchingNextPage, error } = list;
  const hasRows = list.pinned.length > 0 || list.dateGroups.length > 0;

  const [draggingId, setDraggingId] = useState<string | null>(null);
  const executionById = useMemo(() => {
    const map = new Map<string, WorkflowExecution>();
    list.pinned.forEach(execution => map.set(String(execution.id), execution));
    list.dateGroups.forEach(({ executions }) => {
      executions.forEach(execution => map.set(String(execution.id), execution));
    });
    return map;
  }, [list.pinned, list.dateGroups]);
  const draggingExecution = draggingId ? (executionById.get(draggingId) ?? null) : null;

  /** A 6px distance threshold rather than immediate activation, so a plain click on a row still navigates instead of starting a drag. */
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setDraggingId(String(event.active.id));
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setDraggingId(null);
      const { active, over } = event;
      if (!over) return;

      const wasPinned = Boolean((active.data.current as { pinned?: boolean } | undefined)?.pinned);
      const id = String(active.id);
      if (over.id === 'pinned' && !wasPinned) actions.setPinned(id, true);
      else if (over.id === 'unpinned' && wasPinned) actions.setPinned(id, false);
    },
    [actions],
  );

  const handleDragCancel = useCallback(() => setDraggingId(null), []);

  const onIntersect = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage],
  );

  /** Stops observing once a page has failed, so a stale `hasNextPage` cannot loop fetch-fail-refetch with no backoff. */
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || error) return;
    const observer = new IntersectionObserver(onIntersect, { rootMargin: '120px' });
    observer.observe(node);
    return () => observer.disconnect();
  }, [onIntersect, error]);

  // No chat agent for this workspace — the tab is still in the strip, so say why
  // rather than showing a blank panel.
  if (!workflowInstanceId) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 px-4 py-12 text-v2-sidebar-foreground/70">
        <MessageSquare size={20} />
        <p className="text-pretty text-center text-v2-xs">Chat isn&apos;t set up for this workspace yet</p>
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <div className="flex h-full min-h-0 flex-col gap-0.5 overflow-x-clip">
        <ChatSearch value={list.search} onChange={list.setSearch} />

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
          {list.isLoading ? (
            <div role="status" aria-label="Loading chats" className="grid place-items-center py-12">
              <Loader />
            </div>
          ) : error && !hasRows ? (
            <ErrorState />
          ) : list.isEmpty ? (
            <EmptyState isSearching={list.isSearching} />
          ) : (
            <div className="flex flex-col">
              <ChatGroup
                group="pinned"
                executions={list.pinned}
                workflowInstanceId={workflowInstanceId}
                activeExecutionId={executionId}
                isDragActive={draggingId !== null}
                hasError={Boolean(list.pinnedError)}
                search={list.appliedSearch}
                onRename={actions.startRename}
                onDelete={actions.startDelete}
                onSetPinned={actions.setPinned}
              />
              <UnpinnedDropZone>
                {list.dateGroups.map(({ group, executions }) => (
                  <ChatGroup
                    key={group}
                    group={group}
                    executions={executions}
                    workflowInstanceId={workflowInstanceId}
                    activeExecutionId={executionId}
                    isDragActive={draggingId !== null}
                    search={list.appliedSearch}
                    onRename={actions.startRename}
                    onDelete={actions.startDelete}
                    onSetPinned={actions.setPinned}
                  />
                ))}
              </UnpinnedDropZone>
            </div>
          )}

          <div ref={sentinelRef} aria-hidden className="h-1" />
          {isFetchingNextPage ? (
            <div role="status" aria-label="Loading more chats" className="grid place-items-center py-3">
              <Loader />
            </div>
          ) : null}
          {/* A later page failing keeps the rows already on screen; this reports the gap in place instead of replacing them. */}
          {error && hasRows ? (
            <p role="status" className="text-pretty px-4 py-3 text-center text-v2-xs text-v2-sidebar-foreground/70">
              Could not load more chats
            </p>
          ) : null}
        </div>

        <RenameDialog
          execution={actions.renaming}
          isSaving={actions.isRenaming}
          onCancel={actions.cancelRename}
          onSave={actions.rename}
        />
        <DeleteDialog
          open={actions.deletingId !== null}
          count={1}
          pending={actions.isDeleting}
          onCancel={actions.cancelDelete}
          onConfirm={actions.confirmDelete}
        />
      </div>

      {/* Portalled out of the tab panel: `TabsContent` animates on `y`, so it
          keeps a transform and becomes the containing block for anything
          `fixed` inside it — which would seat this overlay against the panel's
          own corner rather than the viewport, leaving it trailing the pointer
          by the panel's offset. */}
      {createPortal(
        <DragOverlay dropAnimation={reduce ? null : DROP_ANIMATION}>
          {draggingExecution ? <DragPreviewRow execution={draggingExecution} /> : null}
        </DragOverlay>,
        document.body,
      )}
    </DndContext>
  );
}
